import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import Popover, { PopoverMenuItem, PopoverDivider } from './Popover';
import { clearAuthSession, getMetadata } from '../utils/encryptedStorage';
import OpenAIKeyWarning from './OpenAIKeyWarning';

const SunIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const SignOutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <circle cx="4" cy="4" r="1.5" />
    <circle cx="10" cy="4" r="1.5" />
    <circle cx="16" cy="4" r="1.5" />
    <circle cx="4" cy="10" r="1.5" />
    <circle cx="10" cy="10" r="1.5" />
    <circle cx="16" cy="10" r="1.5" />
    <circle cx="4" cy="16" r="1.5" />
    <circle cx="10" cy="16" r="1.5" />
    <circle cx="16" cy="16" r="1.5" />
  </svg>
);

/**
 * AdminHeader — Simplified top bar for admin pages.
 * - Logo + app name (links to /admin)
 * - OpenAI status warning
 * - 9-dot grid icon (no popover)
 * - Theme toggle
 * - Account button with logout
 */
const AdminHeader = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { isDark, toggleTheme } = useTheme();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const appName = settings.app?.name || 'Wizbox';
  const appLogo = settings.app?.logo || '/config/logo.svg';

  const metadata = getMetadata();
  const userName = metadata?.name || 'Admin';
  const userEmail = metadata?.email || '';
  const userInitial = userName.charAt(0).toUpperCase();

  const handleSignOut = () => {
    clearAuthSession();
    navigate('/auth/login');
  };

  return (
    <div
      className="sticky top-0 z-30 border-b border-gray-200 dark:border-gray-700 transition-colors duration-250"
      style={{ backgroundColor: isDark ? '#111827' : '#FFFFFF' }}
    >
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left — Grid icon + Logo */}
        <div className="flex items-center gap-3">
          {/* 9-dot grid */}
          <button
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Menu"
          >
            <GridIcon />
          </button>

          {/* Logo + App Name */}
          <Link to="/admin" className="flex items-center gap-2 group">
            <img
              src={appLogo}
              alt={appName}
              className="w-8 h-8 object-contain transition-transform duration-250 group-hover:scale-105 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-250">
              {appName}
            </span>
          </Link>

          <OpenAIKeyWarning />
        </div>

        {/* Right — Theme + Account */}
        <div className="flex items-center gap-1">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250 opacity-80 hover:opacity-100"
            aria-label="Toggle theme"
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Account Popover */}
          <Popover
            position="bottom-right"
            trigger={
              <div className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  {userInitial}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{userName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Admin</p>
                </div>
                <svg className="w-4 h-4 text-gray-400 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            }
            contentClassName="w-56 min-w-[200px]"
          >
            {({ close }) => (
              <>
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{userName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail}</p>
                </div>
                <div className="p-2">
                  <PopoverDivider />
                  <PopoverMenuItem
                    icon={<SignOutIcon />}
                    danger
                    onClick={() => { close(); handleSignOut(); }}
                  >
                    Sign Out
                  </PopoverMenuItem>
                </div>
              </>
            )}
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default AdminHeader;
